import { useEffect, useMemo, useState, memo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Switch,
  Box,
  Divider,
  Paper,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { UserService, CreateUserRequest } from '../../services/userService';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: CreateUserRequest) => void;
}

interface CreateUserFormData {
  email: string;
  password: string;
  displayName?: string;
  role: 'fleetManager' | 'trader';
  admin: boolean;
}

export const CreateUserDialog = memo(({ open, onClose, onCreate }: CreateUserDialogProps) => {
  const { register, handleSubmit, formState, reset, watch } = useForm<CreateUserFormData>({
    defaultValues: { role: 'fleetManager', admin: false },
  });

  const role = watch('role');
  const admin = watch('admin');

  const [customPermissions, setCustomPermissions] = useState<
    Record<string, Record<string, boolean>>
  >({});

  useEffect(() => {
    if (role === 'fleetManager') {
      const tpl = UserService.generateFleetManagerPermissions();
      setCustomPermissions(tpl.fleetPermissions || {});
    } else if (role === 'trader') {
      const tpl = UserService.generateTraderPermissions();
      setCustomPermissions(tpl.traderPermissions || {});
    }
  }, [role]);

  const submitHandler = useCallback(
    (data: CreateUserFormData) => {
      const payload: CreateUserRequest =
        role === 'trader'
          ? {
              email: data.email,
              password: data.password,
              displayName: data.displayName,
              role: 'trader',
              admin,
              trader: true,
              traderPermissions: customPermissions,
            }
          : {
              email: data.email,
              password: data.password,
              displayName: data.displayName,
              role: 'fleetManager',
              admin,
              fleetManager: true,
              fleetPermissions: customPermissions,
            };

      onCreate(payload);
    },
    [role, admin, customPermissions, onCreate]
  );

  const onSubmit = handleSubmit(submitHandler);

  const permissionsResources = useMemo(
    () => [
      'contracts',
      'invoices',
      'vessels',
      'voyages',
      'charterers',
      'procurements',
      'pooling_contracts',
      'notifications',
      'users',
      'entities',
      'reports',
      'fueleuCalculations',
    ],
    []
  );

  const togglePermission = useCallback((resource: string, op: string, value: boolean) => {
    setCustomPermissions((prev) => ({
      ...prev,
      [resource]: { ...prev[resource], [op]: value },
    }));
  }, []);

  const handleClose = useCallback(() => {
    reset();
    setCustomPermissions({});
    onClose();
  }, [reset, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>New User</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
          <TextField label="Email" {...register('email', { required: true })} fullWidth />
          <TextField
            label="Password"
            type="password"
            {...register('password', { required: true, minLength: 6 })}
            fullWidth
          />
          <TextField
            label="Display Name"
            {...register('displayName')}
            fullWidth
            sx={{ gridColumn: '1 / span 2' }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Role
        </Typography>
        <FormControl>
          <RadioGroup row {...register('role')}>
            <FormControlLabel value="fleetManager" control={<Radio />} label="Fleet Manager" />
            <FormControlLabel value="trader" control={<Radio />} label="Trader" />
          </RadioGroup>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch checked={admin} {...register('admin')} />}
            label="Admin"
          />
        </Box>

        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Permissions
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 2,
            }}
          >
            {permissionsResources.map((resource) => (
              <Box key={resource}>
                <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
                  {resource}
                </Typography>
                {['view', 'create', 'update', 'delete'].map((op) => (
                  <FormControlLabel
                    key={op}
                    control={
                      <Switch
                        size="small"
                        checked={customPermissions[resource]?.[op] || false}
                        onChange={(e) => togglePermission(resource, op, e.target.checked)}
                      />
                    }
                    label={op}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" disabled={!formState.isDirty}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
});
